import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Download, KanbanSquare, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Pagination, Spinner } from "@/components/common/Common";
import { FilterChip } from "@/components/common/FilterChip";
import { DaysRemaining, StatusBadge } from "@/components/common/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { useList } from "@/hooks/useList";
import { toOpts, useLookups } from "@/hooks/useLookups";
import { api, fmtDate, formatError } from "@/lib/api";
import { AU_STATES, STATUS_LABELS, opts } from "@/lib/constants";

const EMPTY = { status: [], recruitment_company_id: [], work_state: [], assigned_to: [] };

function SavedFilters({ filters, apply }) {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/saved-filters", { params: { page: "contracts" } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/saved-filters", { name, page: "contracts", filters }); setName(""); toast.success("Filter saved"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const remove = async (id) => { await api.delete(`/saved-filters/${id}`); load(); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-300" data-testid="saved-filters-button">
          <Bookmark className="h-3 w-3" /> Saved filters
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 space-y-3" align="start">
        <div className="space-y-1">
          {list.map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded hover:bg-slate-50 px-2 py-1">
              <button type="button" className="text-sm text-slate-700 text-left flex-1" onClick={() => apply(f.filters)} data-testid={`saved-filter-${f.id}`}>{f.name}</button>
              <button type="button" onClick={() => remove(f.id)} data-testid={`saved-filter-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" /></button>
            </div>
          ))}
          {!list.length && <p className="text-xs text-slate-400 px-2">No saved filters yet</p>}
        </div>
        <div className="flex gap-2 border-t border-slate-100 pt-3">
          <Input placeholder="Name current filter" value={name} onChange={(e) => setName(e.target.value)} className="h-8" data-testid="saved-filter-name-input" />
          <Button size="sm" disabled={!name.trim()} onClick={save} data-testid="saved-filter-save-button">Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

async function downloadCsv(params) {
  const res = await api.get("/contracts/export", { params, responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  Object.assign(document.createElement("a"), { href: url, download: `contracts-${new Date().toISOString().slice(0, 10)}.csv` }).click();
  URL.revokeObjectURL(url);
}

function BulkBar({ selected, clear, staffOpts, reload, params }) {
  const run = async (action, value) => {
    try { const { data } = await api.post("/contracts/bulk", { ids: selected, action, value }); toast.success(`${data.updated} contract(s) updated`); clear(); reload(); } catch (e) { toast.error(formatError(e)); }
  };
  const exportSel = async () => { try { await downloadCsv({ ...params, ids: selected.join(",") }); toast.success("Export downloaded"); } catch (e) { toast.error(formatError(e)); } };
  return (
    <div className="sticky top-[110px] z-20 flex flex-wrap items-center gap-3 bg-blue-600 text-white rounded-lg px-4 py-2 mb-3 shadow-md animate-fade-up" data-testid="bulk-bar">
      <span className="text-sm font-medium">{selected.length} selected</span>
      <Select onValueChange={(v) => run("assign", v === "none" ? null : v)}>
        <SelectTrigger className="h-8 w-44 bg-white text-slate-800" data-testid="bulk-assign-select"><SelectValue placeholder="Assign to…" /></SelectTrigger>
        <SelectContent><SelectItem value="none">Unassigned</SelectItem>{staffOpts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
      <Select onValueChange={(v) => run("status", v)}>
        <SelectTrigger className="h-8 w-48 bg-white text-slate-800" data-testid="bulk-status-select"><SelectValue placeholder="Change status…" /></SelectTrigger>
        <SelectContent>{Object.entries(STATUS_LABELS).filter(([k]) => k !== "inactive").map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" variant="secondary" onClick={exportSel} data-testid="bulk-export-button"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
      <button type="button" className="ml-auto" onClick={clear} data-testid="bulk-clear"><X className="h-4 w-4" /></button>
    </div>
  );
}

export default function ContractsList() {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const lk = useLookups();
  const [filters, setFilters] = useState(EMPTY);
  const [sp, setSp] = useSearchParams();
  const queue = sp.get("queue");
  const [queues, setQueues] = useState({});
  useEffect(() => { api.get("/dashboard/queues").then((r) => setQueues(r.data)); }, []);
  const [selected, setSelected] = useState([]);
  const params = { ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v.length).map(([k, v]) => [k, v.join(",")])), ...(queue ? { queue } : {}) };
  const list = useList("/contracts", { filters: params });
  const staffOpts = [{ value: "unassigned", label: "Unassigned" }, ...toOpts(lk?.staff)];
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOnPage = list.items.length > 0 && list.items.every((r) => selected.includes(r.id));
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const exportAll = async () => { try { await downloadCsv({ ...params, ...(list.q ? { q: list.q } : {}) }); toast.success("Export downloaded"); } catch (e) { toast.error(formatError(e)); } };

  return (
    <div>
      <PageHeader title="Contracts" subtitle="Every placement, sorted by end date." testId="contracts-header"
        actions={<>
          <Button variant="outline" onClick={() => navigate("/contracts/board")} data-testid="board-view-button"><KanbanSquare className="h-4 w-4 mr-1" />Board view</Button>
          {canWrite && <>
          <Button variant="outline" onClick={exportAll} data-testid="export-csv-button"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
          <Button onClick={() => navigate("/contracts/new")} data-testid="new-contract-button"><Plus className="h-4 w-4 mr-1" />New contract</Button>
          </>}
        </>} />
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <Input placeholder="Search employment ID or contractor…" value={list.q} onChange={(e) => list.setQ(e.target.value)} className="lg:max-w-xs bg-white" data-testid="contract-search-input" />
        <div className="flex flex-wrap gap-2">
          <FilterChip label="Status" options={opts(STATUS_LABELS)} value={filters.status} onChange={set("status")} testId="filter-status" />
          <FilterChip label="Recruitment company" options={toOpts(lk?.recruitment_companies)} value={filters.recruitment_company_id} onChange={set("recruitment_company_id")} testId="filter-company" />
          <FilterChip label="State" options={AU_STATES.map((s) => ({ value: s, label: s }))} value={filters.work_state} onChange={set("work_state")} testId="filter-state" />
          <FilterChip label="Assigned to" options={staffOpts} value={filters.assigned_to} onChange={set("assigned_to")} testId="filter-assigned" />
          {queue && <button type="button" onClick={() => setSp({})} className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white px-3 py-1.5 text-xs font-medium" data-testid="queue-chip">{queues[queue] || queue}<X className="h-3 w-3" /></button>}
          <SavedFilters filters={filters} apply={(f) => setFilters({ ...EMPTY, ...f })} />
          {Object.values(filters).some((v) => v.length) && <button type="button" className="text-xs text-slate-500 hover:text-slate-800" onClick={() => setFilters(EMPTY)} data-testid="filters-clear-all">Clear all</button>}
        </div>
      </div>
      {canWrite && selected.length > 0 && <BulkBar selected={selected} clear={() => setSelected([])} staffOpts={toOpts(lk?.staff)} reload={list.reload} params={params} />}
      {list.loading && !list.items.length ? <div className="flex justify-center p-8"><Spinner /></div> : (
        <>
          <div className="hidden md:block bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead><tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
                {canWrite && <th className="px-4 py-3 w-10"><Checkbox checked={allOnPage} onCheckedChange={(c) => setSelected(c ? [...new Set([...selected, ...list.items.map((r) => r.id)])] : selected.filter((id) => !list.items.some((r) => r.id === id)))} data-testid="select-all-checkbox" /></th>}
                {["Employment ID", "Contractor", "Recruitment company", "Status", "Start", "End", "Days left", "Assigned to"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {list.items.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-blue-50/40 cursor-pointer" onClick={() => navigate(`/contracts/${r.id}`)} data-testid={`contract-row-${r.id}`}>
                    {canWrite && <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`contract-select-${r.id}`} /></td>}
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">{r.employment_id}</td>
                    <td className="px-4 py-3 text-slate-800">{r.contractor_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.recruitment_company_name || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(r.start_date)}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(r.end_date)}</td>
                    <td className="px-4 py-3"><DaysRemaining days={r.days_remaining} status={r.status} /></td>
                    <td className="px-4 py-3 text-slate-600">{r.assigned_to_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!list.items.length && <p className="p-10 text-center text-sm text-slate-500" data-testid="empty-state">No contracts match these filters</p>}
          </div>
          <div className="md:hidden space-y-3">
            {list.items.map((r) => (
              <Link key={r.id} to={`/contracts/${r.id}`} className="block bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid={`contract-card-${r.id}`}>
                <div className="flex items-center justify-between"><span className="font-mono text-xs font-semibold">{r.employment_id}</span><StatusBadge status={r.status} /></div>
                <p className="font-semibold text-slate-900 mt-1">{r.contractor_name}</p>
                <p className="text-xs text-slate-500">{r.recruitment_company_name}</p>
                <p className="text-xs text-slate-500 mt-1">{fmtDate(r.start_date)} – {fmtDate(r.end_date)} · <DaysRemaining days={r.days_remaining} status={r.status} /> days</p>
              </Link>
            ))}
          </div>
        </>
      )}
      <Pagination {...list} testId="contracts-pagination" />
    </div>
  );
}
